/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { Link } from '@ta-interaktiv/contentbricks'
import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'
import { ArticleTeasers } from '@ta-interaktiv/react-article-teasers'
import { parseHostname } from '@ta-interaktiv/parse-hostname'

const ADDITIONAL_SHARES = 39 + 554 + 639
const ARTICLE_ID = '29899036'
const isMobile = window.innerWidth < 600

export function AppComponent() {
  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} additionalShares={ADDITIONAL_SHARES} />
      <ThemeSwitch />

      <header className='ui vertical very fitted segment'>
        <div className='ui container'>
          <h1 className='ui image header'>
            <div className='content'>
              Drei Covid-19-Szenarien für die Schweiz
              <div className='sub header'>
                Wir haben berechnet, wie sich die Krankheit Covid-19 in der
                Schweiz ausbreiten könnte. Die Prognosemodelle zeigen klar: Um
                das Virus einzudämmen, braucht es drastische und schnelle
                Massnahmen.
              </div>
            </div>
          </h1>
        </div>
      </header>

      {/* TODO And probably remove the following segment and replace it with
       your own content */}
      <div className='ui vertical segment'>
        <div className='ui text container'>
          <p className='ta author'>M. Brupbacher, A. Moor</p>
          <p style={{fontSize: '0.9rem', color: '#888'}}><em>
                Anmerkung: Dieser Artikel wurde am 12. März noch vor dem
                Ausrufen des Notstandes in der Schweiz vom 17. März publiziert.
                Am 21. März wurde das Szenario 2 gemäss den bestätigten Fallzahlen schon
                überschritten. Es läuft also auf ein Szenario hinaus, dass sich
                irgendwo zwischen Szenario 1 und 2 bewegen wird.</em>
              </p>
          <p>
            Die Menschen sind lineares Wachstum gewohnt, das können sie
            verstehen. Exponentielles Wachstum, also eine Vermehrung, die sich
            ständig beschleunigt, ist schwierig zu fassen. Und das macht die
            Covid-19-Verbreitung so gefährlich.{' '}
            <strong>
              Zu Beginn steigt es gemächlich, dann schiesst es nach oben.
            </strong>{' '}
            Die Kurve der Corona-Fälle sieht in allen Ländern mit grösseren
            Ausbrüchen sehr ähnlich aus. Der Schweizer Epidemienforscher
            Christian Althaus{' '}
            <strong>
              schätzt eine Verdopplungszeit auch in der Schweiz von nur wenigen
              Tagen
            </strong>
            . Ende März hätten wir so mehr als 10’000 Fälle, danach ginge es
            noch steiler nach oben. Bei ungebremstem Wachstum wäre bald ein
            grosser Teil der Menschen in der Schweiz mit dem Virus infiziert.
          </p>
          <p>
            Der Basler Professor am Biozentrum der Universität Basel, Richard
            Neher, hat mit Kollegen ein{' '}
            <Link href='https://twitter.com/richardneher/status/1236980631789359104?s=20'>
              Tool
            </Link>{' '}
            konzipiert, mit welchem sich verschiedene Szenarien bei einem
            Covid-19-Ausbruch simulieren lassen. Wir haben damit drei Szenarien
            für die Schweiz entworfen.
          </p>
          <p>
            Bitte beachten:{' '}
            <strong>
              Die berechneten Szenarien sind Modelle, kein Blick in die Zukunft.
            </strong>{' '}
            Sie sollen das Prinzip und die Auswirkungen von
            Eindämmungsmassnahmen veranschaulichen. Es gibt nach wie vor viele
            Unbekannte (
            <Link
              allowWordWrap
              href='https://interaktiv.tagesanzeiger.ch/2020/neue-wissenschaftliche-erkenntnisse-zum-coronavirus/?nosome'
            >
              Zum aktuellen Stand der Wissenschaft
            </Link>
            ). Richard Neher sagt zu unseren berechneten Szenarien: «Die
            Parameter machen schon Sinn. Die einfachen Modelle haben aber die
            Tendenz, die Gesamtinfektionen zu überschätzen.»
          </p>
        </div>
      </div>

      <div className='ui vertical segment'>
        <div className='ui text container infographic'>
          <h3 className='ui dividing infographic'>
            <strong>Szenario 1</strong>
            <br />
            Ungebremstes Wachstum
          </h3>
          <p>
            Wir berücksichtigen die Schweizer Bevölkerung mit ihren rund 8,6
            Millionen Einwohnern und ihrer Altersstruktur. Wir lassen die
            Simulation bis Ende Jahr laufen. Der Schlüsselwert ist der R0-Wert.
            Er bestimmt die Ansteckungsgefahr eines Virus, also, wie viele
            ­weitere Menschen ein einzelner Infizierter ansteckt. Die ETH
            berechnete mit einem Computermodell, dass eine mit dem Coronavirus
            infizierte Person im Durchschnitt zwischen 2 und 3,5 weitere
            Menschen ansteckt. Wir nehmen für all unsere Modellrechnungen einen
            R0-Wert von 2,7. Der zweite wichtige Wert für die Berechnung ist die
            Sterberate. Neher hat diese Zahl fix im Tool mit 0,97% verbaut.
            Diesen Wert hat auch{' '}
            <Link href='https://twitter.com/AdamJKucharski/status/1235700923978129408?s=20'>
              Adam Kucharski
            </Link>
            , Mathematiker und Epidemiologe an der London School of Hygiene &
            Tropical Medicine, kürzlich so berechnet. Wie sich das wie stark auf
            welche Altersgruppen auswirkt, wurde{' '}
            <Link href='https://interaktiv.tagesanzeiger.ch/2020/coronavirus-analyse-sterblichkeit/?nosome'>
              mit Daten
            </Link>{' '}
            der bisher grössten Fallzahlen-Studie aus China berechnet. Zudem
            haben wir einen mittelstarken Saison-Effekt definiert, der die
            Fallzahlen im Frühling/Sommer um 20% drücken kann – das aber ist nur
            ein Tropfen auf den heissen Stein (
            <Link
              allowWordWrap
              href='https://interaktiv.tagesanzeiger.ch/2020/neue-wissenschaftliche-erkenntnisse-zum-coronavirus/?nosome'
            >
              Zum aktuellen Stand der Wissenschaft
            </Link>
            ). Die Werte zur Latenzzeit und dem Infektionszeitraum belassen wir
            so, wie sie die Wissenschaftler für Covid-19 im Tool vordefiniert
            haben.
          </p>
          <div style={{ margin: '30px 0 10px 0' }}>
            <AutofitIFrame src='https:////datawrapper.dwcdn.net/kEBhd/16/' />
          </div>
          <div className='box'>
            <p>
              Das Resultat ist ein Schock. Hätte man überhaupt keine
              Eindämmungsmassnahmen getroffen und das Virus einfach durch die
              nicht durch Immunität und Impfung geschützte Bevölkerung laufen
              lassen,{' '}
              <strong>
                wären Ende April beim Höhepunkt gleichzeitig 130’000 Personen
                schwer oder kritisch krank gewesen und damit das Schweizer
                Gesundheitssystem mit seinen{' '}
                <Link href='https://www.nzz.ch/schweiz/coronavirus-schweizer-spitaelern-droht-der-kollaps-ld.1546008'>
                  1000 bis 1750 Betten
                </Link>{' '}
                auf Intensivstationen komplett überlastet
              </strong>
              . Im Juni wären bereits über 80% der Bevölkerung infiziert
              gewesen. Insgesamt hätte es gemäss dieser Modellrechnung über
              70’000 Tote gegeben. Im Sommer wäre die Seuche praktisch vorbei
              gewesen mit nur noch einer Handvoll neuer Fälle pro Tag – die
              Herdenimmunität würde wirken. Neher sagt: «Ohne Massnahmen wird
              sich ein grosser Teil der Bevölkerung infizieren. Ob das nun 20,
              40 oder 60% sind, wissen wir nicht.»
            </p>
          </div>
        </div>
      </div>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <p>
            Zwar verläuft die Krankheit bei der Mehrzahl der Infizierten{' '}
            <Link href='https://interaktiv.tagesanzeiger.ch/2020/coronavirus-analyse-sterblichkeit/?nosome'>
              relativ harmlos
            </Link>
            . Doch etwa jede fünfte Erkrankung nimmt einen schweren Verlauf, die
            eine Behandlung im Spital erfordert. In Norditalien müssen{' '}
            <Link href='https://mailchi.mp/esicm/the-future-of-haemodynamic-monitoring-first-webinar-of-the-year-1009715'>
              10% der Fälle
            </Link>{' '}
            intensivmedizinisch betreut werden.{' '}
            <strong>
              Sollte das Gesundheitssystem unter der Last zusammenbrechen,
              dürften die Todeszahlen deutlich steigen.
            </strong>{' '}
            «Was mich am meisten beunruhigt, ist die drohende Überlastung der
            Krankenhäuser», sagt Epidemienforscher Christian Althaus von der
            Universität Bern. «Die ist auch für Patienten problematisch, die
            beispielsweise wegen eines Herzinfarkts in Behandlung sind.»
          </p>
          <p>
            Dass dieses erste Szenario oben eintreten wird, ist in der Schweiz
            unwahrscheinlich, da bereits Kontrollmassnahmen durch Bund und
            Kantone ergriffen wurden. Es soll aber verdeutlichen, was ohne
            Gegenmassnahmen hätte passieren können.
          </p>
        </div>
      </div>

      <div className='ui vertical segment'>
        <div className='ui text container infographic'>
          <h3 className='ui dividing infographic'>
            <strong>Szenario 2</strong>
            <br />
            Mittlere bis starke Massnahmen
          </h3>
          <p>
            Alle Einstellungen bleiben gleich wie beim Szenario 1, nur den
            Effekt der Massnahmen erhöhen wir. Durch Hygienebestimmungen, Social
            Distancing und Isolieren der Kranken kann man die Kurven drücken. So
            könnte in unserem Szenario der R0-Wert in der Schweiz schon bald auf
            etwa 1,1 gesenkt werden.
          </p>
          <div style={{ margin: '30px 0 10px 0' }}>
            <AutofitIFrame src='https://datawrapper.dwcdn.net/1gmrK/9/' />
          </div>
          <div className='box'>
            <p>
              Hier ist gut zu sehen, dass die Fallzahlen-Kurve auch so zu Beginn
              stark steigen würde, aber nie höher als 5000 Infizierte
              gleichzeitig.{' '}
              <strong>
                Die Anzahl gleichzeitig schwer und kritisch Erkrankter bliebe
                mit einigen hundert Fällen auf tiefem Niveau stabil, das
                Gesundheitssystem dürfte so nicht kollabieren.
              </strong>{' '}
              Bereits ab Mai könnten so die Infektionszahlen zurückgehen. Bis in
              einem Jahr wären nur rund 20% der Bevölkerung infiziert. Befindet
              sich die Schweiz momentan in diesem Szenario? Wir wissen es nicht,
              niemand kann das zum jetzigen Zeitpunkt sagen, es ist unklar, ob
              und wie die bereits ergriffenen Kontrollmassnahmen in der Schweiz
              greifen. «Der Höhepunkt der Epidemie in der Schweiz könnte Mitte
              Mai sein», beantwortete Patrick Mathys, Leiter Sektion
              Krisenbewältigung und internationale Zusammenarbeit, eine Frage
              eines Journalisten an der Pressekonferenz vom 11. März. Aber: Auch
              bei diesem Szenario könnte es mehrere Tausend Tote geben. Und:{' '}
              <strong>
                Die Kurve flacht nie vollständig ab, sie steigt Ende Jahr sogar
                ziemlich schnell wieder an.
              </strong>{' '}
              «Solange der R0-Wert über 1 liegt kann sich das Virus weiter
              ausbreiten», so Althaus. Und Neher sagt: «Wenn in der Anfangsphase
              der Ausbruch auf viele Tausend Fälle wächst, bleiben die täglichen
              Fallzahlen bei einem R0 um 1 im wesentlichen konstant. Über die
              Zeit führt dies auch zu vielen Fällen.»
            </p>
          </div>
        </div>
      </div>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <p>
            <strong>
              Um das Virus erfolgreich eindämmen zu können, kommt man nicht
              darum herum, den R0-Wert unter 1 zu drücken.
            </strong>{' '}
            So, wie das China und Südkorea mit{' '}
            <Link href='https://www.thelancet.com/journals/lancet/article/PIIS0140-6736(20)30567-5/fulltext'>
              drastischen Massnahmen
            </Link>{' '}
            geschafft haben. In China gab es zu Beginn ebenfalls einen
            exponentiellen Anstieg der Corona-Fälle. Inzwischen sind die
            täglichen Neuinfektionen stark zurückgegangen.
          </p>
          <AutofitIFrame src='https://datawrapper.dwcdn.net/cL4E3/26/' />
          <p>
            Auch in Südkorea beginnen die Massnahmen zu wirken. Die täglichen
            Neuinfektionen sind von über 600 Anfang März zuletzt auf 114 (11.
            März) gefallen – innerhalb von nur 10 Tagen. Bei über 7900
            Erkrankten sind bisher 66 Personen gestorben, das ist eine deutlich
            tiefere Sterberate, als wir sie in Italien beobachten.{' '}
            <strong>
              Entscheidend dazu beigetragen haben gross angelegte Virentests.
              Bis dato wurden in Südkorea mehr als 200’000 von ihnen
              vorgenommen.
            </strong>{' '}
            So liessen sich Infizierte frühzeitig isolieren. In der Schweiz
            haben sich die Behörden jedoch{' '}
            <Link href='https://www.tagesanzeiger.ch/schweiz/standard/die-epidemie-geraet-ausser-kontrolle/story/10974797'>
              dafür entschieden
            </Link>
            , dass Ärzte einen Test auf Covid-19 nur noch bei schweren Fällen
            machen sollen. In Südkorea sind Museen geschlossen, Konzerte werden
            abgesagt, die Semesterferien an Schulen und Universitäten wurden
            verlängert.
          </p>
          <p>
            Die Zahl neuer Infektionsfälle in der sogenannten «roten Zone» in
            der lombardischen Provinz Lodi, in der am 21. Februar die
            Coronavirus-Epidemie ausgebrochen war, hat sich ebenfalls auf Null
            reduziert. «Die strenge Quarantäne, die dort eingeführt wurde, ist
            ein Beispiel und ein Beweis, dass die Massnahmen streng angewendet
            werden müssen», erklärte der lombardische Präsident Attilio Fontana
            am 13. März.
          </p>
          <p>
            Was strenge Massnahmen bewirken können, illustriert das letzte
            Szenario.
          </p>
        </div>
      </div>

      <div className='ui vertical segment'>
        <div className='ui text container infographic'>
          <h3 className='ui dividing infographic'>
            <strong>Szenario 3</strong>
            <br />
            Drastische Massnahmen
            <br />
            wie in China und Südkorea
          </h3>
          <p>
            Alle Parameter bleiben gleich wie beim Szenario 1, nur den Effekt
            der Massnahmen erhöhen wir erneut. Durch Hygienemassnahmen, Social
            Distancing und Isolieren der Kranken kann man die Kurve noch stärker
            drücken. Ab April könnte so der R0-Wert auf etwa 0,8 gedrückt
            werden, also unter die entscheidende Grösse von 1. Zum Vergleich:
            «Die aktuellsten R0-Werte in Wuhan werden mit 0,3 angegeben», sagt
            Epidemiologe Althaus.
          </p>
          <div style={{ margin: '30px 0 10px 0' }}>
            <AutofitIFrame src='https://datawrapper.dwcdn.net/25QTX/10/' />
          </div>
          <div className='box'>
            <p>
              <strong>
                Wenn es gelingt, durch Massnahmen den R0-Wert unter 1 zu
                drücken, kann die Epidemie stark eingedämmt werden.
              </strong>{' '}
              Im Maximum wären so in der Schweiz Mitte April nur 1800 Personen
              gleichzeitig infiziert. Die Anzahl kritisch Erkrankter bliebe
              überschaubar. Die Todesfälle lägen im Bereich einer stärkeren
              saisonalen Grippe. «Aber ein solcher Wert lässt sich nur mit
              drastischen Massnahmen erreichen – in der Schweiz sind wir noch
              weit davon entfernt», sagt Marcel Salathé, Epidemiologie-Professor
              an der ETH in Lausanne.
            </p>
          </div>
        </div>
      </div>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <p>
            Doch auch beim Modell mit extremen Massnahmen gibt es ein Problem:{' '}
            <strong>
              Sobald die Quarantänemassnahmen aufgehoben werden, kann die
              Ansteckungsrate wieder steigen.
            </strong>{' '}
            Eine Lehre aus der Spanischen Grippe in den Jahren 1918 bis 1920
            ist, dass sich die Fallzahlen rasch wieder erhöhen, wenn die
            Massnahmen gelockert werden. Dann kann eine Epidemie auf einen
            zweiten Höhepunkt zusteuern. Das Virus ist ja immer noch da.
          </p>
          <p>
            Der amerikanische Epidemiologe Marc Lipsitch sagte gegenüber dem{' '}
            <Link href='https://www.spiegel.de/wissenschaft/medizin/coronavirus-in-manchen-gebieten-ausser-kontrolle-a-00000000-0002-0001-0000-000169828728'>
              «Spiegel»
            </Link>
            : «Auf lange Sicht werden sich möglicherweise ähnlich viele Menschen
            infizieren wie ohne diese Eindämmung, aber so verlangsamen wir die
            Ausbreitung entscheidend.» Warum das so wichtig ist, erklärt er so:
            «Erstens haben Spitäler und Ärzte in allen Ländern eine begrenzte
            Kapazität, und diese sollten wir nicht sprengen. Und zweitens lernen
            wir mit jeder Woche besser, welche Therapien bei Covid-19
            anschlagen, und finden möglicherweise wirksame Medikamente.{' '}
            <strong>
              Wenn Sie die Wahl haben, dann erkranken Sie lieber in sechs
              Monaten als heute.
            </strong>
            »
          </p>
          <p>
            Gegenüber dem «Tages-Anzeiger» sagte Lipsitch: «Praktisch alle
            Experten gehen davon aus, dass auch in Ländern, die jetzt erste
            wenige Tote verzeichnen, wie der Schweiz, die Todesfälle bald
            exponentiell anwachsen werden. Natürlich hoffen alle, dass man etwas
            übersehen hat und am Schluss viel weniger Opfer hat.{' '}
            <strong>
              Aber genauso gut könnten wir etwas übersehen, das die Lage noch
              schlimmer macht.
            </strong>
            »
          </p>
          <p>
            Es kommt darauf an, die Kurve so früh wie möglich zum Abflachen zu
            bringen und darauf zu hoffen, dass möglichst schnell ein Impfstoff
            entwickelt wird.
          </p>
          <p>
            Deshalb fordert auch Althaus, dass man die Einführung drastischerer
            Kontrollmassnahmen in Betracht zieht.{' '}
            <strong>
              «China und Italien sind uns einige Tage voraus, deshalb sollten
              wir von den Erfahrungen dieser Länder lernen und lieber jetzt als
              zu spät handeln.»
            </strong>
          </p>
          <p>
            Der WHO-Epidemiologe Bruce Aylward sagt: «Das Ziel muss sein, die
            Hitze rauszunehmen, das Geschehen zu verlangsamen, damit es möglich
            wird, die am meisten gefährdeten Menschen in der Bevölkerung zu
            schützen und die Wirtschaft am Laufen zu halten.»{' '}
            <strong>
              Am wichtigsten sei, die betroffenen Kranken und ihre engen
              Kontakte extrem schnell zu finden, extrem schnell zu testen und
              extrem schnell zu isolieren.
            </strong>
            Zudem sei wichtig, dass man bei Krankheitssymptomen nicht unter die
            Menschen gehe und auch sonst auf enge soziale Kontakte generell
            verzichte: Kein Händeschütteln, keine Umarmungen, kein Küssen und
            sich gut die Hände waschen. «Das müssen wir jetzt alle üben. Und
            bereit sein, Versammlungen jeder Art abzusagen oder zu unterbinden»,
            sagt er.
          </p>
          <p>
            Und weiter: «Wir müssen jetzt im Westen die Ärmel hochkrempeln und
            uns an die Arbeit machen: In China wurden keine Massnahmen
            ergriffen, die nicht auch anderswo umgesetzt werden könnten.»
          </p>
          <p>
            Ein direkter Vergleich der Fallzahlen der drei Modelle verdeutlicht
            die Dringlichkeit griffiger Massnahmen. Während die Zahl der
            Infizierten in Modell 1 nach oben schiesst und bei Modell 2 nie ganz
            verschwindet, können drastische Massnahmen in Modell 3 die
            Ansteckungsgefahr stark reduzieren und ein erneutes Ansteigen der
            Fallzahlen verhindern.
          </p>
          <AutofitIFrame src='https://datawrapper.dwcdn.net/Z5pTe/8/' />
        </div>
      </div>

      <div className='ui vertical segment'>
        <div className='ui text container infographic'>
          <div className='box'>
            <h4 className='ui small infographic header'>
              Achtung bei Prognosen
            </h4>
            <p>
              Manuel Battegay, Professor für Infektiologie an der Universität
              Basel, hat sich unsere Szenarien angeschaut und mahnt bei der
              Interpretation zur Vorsicht: «Nach wie vor gibt es grosse
              Unbekannte, beispielsweise die wirkliche Anzahl von infizierten
              Menschen, die wahrscheinlich immun sind und andere schützen können
              (Herdenimmunität). Auch die Transmissionsrate (R0) kann stark im
              Verlauf variieren. Wir kennen auch das Mass der Saisonalität
              nicht, das heisst wie stark die Pandemie im Sommer/Frühling
              abflauen wird.{' '}
              <strong>
                Daher sind Prognosen schwierig. Aber die Szenarien zeigen
                eindrücklich wie wesentlich Präventionsmassnahmen sind.
              </strong>{' '}
              In China, insbesondere der Provinz Hubei mit 60-70 Millionen
              Einwohnern, sind dadurch nicht noch schlimmere Szenarien
              eingetroffen. Beim Nachlassen der Massnahmen in China werden wir
              bald sehen, ob es zu einem Wiederaufflammen kommt oder nicht.
              Falls nicht, ist anzunehmen, dass doch ein grösserer Teil der
              Bevölkerung schon durchseucht wurde und die Herdenimmunität jetzt
              schützt. Auch wenn wir mit Modellen vorsichtig sein müssen, sind
              sie sehr wertvoll, auch um die zeitliche Dimensionen der Epidemie
              abzuschätzen. Der kumulative Effekt von getroffenen
              Präventionsmassnahmen ist gross. Jede vermeidbare Infektion hilft
              also, dass wir keine zu hohe Zahl schwer und kritisch kranker
              Patienten haben, die unser Gesundheitswesen überfordern wird.»
            </p>
          </div>
        </div>
      </div>

      <div className='ui vertical segment'>
        <div className='ui text container infographic'>
          <div
            style={{
              maxWidth: 375,
              margin: 'auto',
              borderTop: '5px solid #f7cb4a',
              background: '#fff8db',
              padding: 15
            }}
          >
            <div
              style={{
                width: '100%',
                margin: '15px 0 0 0',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <img
                width='60%'
                src={`//interaktiv.tagesanzeiger.ch/static/logos/${
                  parseHostname().publicationName
                }.svg`}
              />
            </div>
            <h4 className='ui small infographic header'>
              Tagespass verschenken
            </h4>
            <p>
              Hat Ihnen dieser Beitrag gefallen? Dann können Sie ihn gerne
              weiterempfehlen. Mit dem Gutscheincode <strong>24h</strong> kann
              jeder und jede für einen Tag Abo+-Inhalte lesen. Einfach unter{' '}
              <Link
                href={`https://aboshop.${parseHostname().publicationDomain}`}
              >{`aboshop.${parseHostname().publicationDomain}`}</Link>{' '}
              einlösen.
            </p>
          </div>
        </div>
      </div>

      {/* TODO Add your credits in this component */}
      <Footer
        articleId={ARTICLE_ID}
        additionalShares={ADDITIONAL_SHARES}
        credits={[
          ['Text', 'Marc Brupbacher'],
          ['Umsetzung', 'Andreas Moor']
        ]}
        questions={['Wie informativ war der Beitrag?']}
      >
        <div
          className='ui container'
          style={{ margin: isMobile ? '1.2em 0 3em' : '0 0 3em' }}
        >
          <h4 className='teaser'>Weitere Beiträge zum Thema</h4>
          <ArticleTeasers
            numberOfCards='three'
            articleIds={[
              '11435150',
              '13939717',
              '19483314',
              '13878467',
              '15650041',
              '31092907',
              '10448437'
            ]}
          />
        </div>
      </Footer>
    </div>
  )
}
